import type { TFieldMapping } from "@/utils/serverErrors";

import {
  normalizeServerValidationErrors,
  serverGeneralErrorKey,
  serverGeneralErrorTranslatedValue,
} from "@/utils/serverErrors";

/**
 * Returns an object containing the server error fields translated to the
 * current language, the key of the server error, and the translation of the
 * server error.
 *
 * @param {unknown} error - The server error object.
 * @param {TFieldMapping} [fieldMapping] - The mapping of server error fields to client fields. Structure: { [ServerFieldName]: "ClientFieldName" }
 * @returns {Object} An object containing the error fields, error key, and error translation.
 */
export const graphqlErrorService = (
  error: unknown,
  fieldMapping?: TFieldMapping
) => {
  const errorFields = normalizeServerValidationErrors(error, fieldMapping);
  return {
    errorFields,
    errorKey: serverGeneralErrorKey(error),
    errorTranslation: serverGeneralErrorTranslatedValue(error),
    hasErrorFields: Object.keys(errorFields).length > 0,
  };
};

/**
 * Used to convert the result of graphqlErrorService into an Error instance.
 */

type GraphqlErrorServiceReturnedType = ReturnType<typeof graphqlErrorService>;

export class GraphqlMappedError extends Error {
  constructor(
    public errorFields: GraphqlErrorServiceReturnedType["errorFields"],
    public errorKey: GraphqlErrorServiceReturnedType["errorKey"],
    public errorTranslation: GraphqlErrorServiceReturnedType["errorTranslation"]
  ) {
    super(errorTranslation ?? "GraphQL error");
  }

  get hasErrorFields(): boolean {
    return Object.keys(this.errorFields).length > 0;
  }

  static from(error: unknown): GraphqlMappedError {
    const mapped = graphqlErrorService(error);

    return new GraphqlMappedError(
      mapped.errorFields,
      mapped.errorKey,
      mapped.errorTranslation
    );
  }
}
